// MediaPipe Vision — face landmarks + selfie segmentation
// Loads lazily on first use, caches the runners across all calls in the session.

import { FaceLandmarker, ImageSegmenter, FilesetResolver } from "@mediapipe/tasks-vision";

const WASM_BASE = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm";
const FACE_MODEL = "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task";
const SEG_MODEL  = "https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_multiclass_256x256/float32/1/selfie_multiclass_256x256.tflite";

let _faceLandmarker = null;
let _segmenter = null;
let _loadingPromise = null;

// Hair label index in MediaPipe Selfie Multiclass model.
// Categories: 0 background, 1 hair, 2 body-skin, 3 face-skin, 4 clothes, 5 others
export const HAIR_LABEL = 1;
export const FACE_SKIN_LABEL = 3;
export const BODY_SKIN_LABEL = 2;

export async function loadVisionRunners() {
  if (_faceLandmarker && _segmenter) return { faceLandmarker: _faceLandmarker, segmenter: _segmenter };
  if (_loadingPromise) return _loadingPromise;
  _loadingPromise = (async () => {
    const fileset = await FilesetResolver.forVisionTasks(WASM_BASE);
    const [face, seg] = await Promise.all([
      FaceLandmarker.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: FACE_MODEL, delegate: "GPU" },
        runningMode: "IMAGE",
        numFaces: 1,
        outputFaceBlendshapes: false,
      }),
      ImageSegmenter.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: SEG_MODEL, delegate: "GPU" },
        runningMode: "IMAGE",
        outputCategoryMask: true,
        outputConfidenceMasks: false,
      }),
    ]);
    _faceLandmarker = face;
    _segmenter = seg;
    return { faceLandmarker: _faceLandmarker, segmenter: _segmenter };
  })();
  return _loadingPromise;
}

// Run both runners on a single image and return geometry the painting code needs.
// Returns: { hairMask: Uint8Array (W*H, 1 where hair, 0 otherwise),
//            faceMask: Uint8Array (W*H, 1 where face-skin / nose / lips / eyes),
//            width, height, faceBox: {x, y, w, h} | null }
export async function analyseImage(imgElement) {
  const { faceLandmarker, segmenter } = await loadVisionRunners();

  // Downscale to 512 px max — MediaPipe segmenter runs at 256, larger input is wasted.
  const SRC_W = imgElement.naturalWidth;
  const SRC_H = imgElement.naturalHeight;
  const maxDim = 512;
  const scale = Math.min(1, maxDim / Math.max(SRC_W, SRC_H));
  const W = Math.round(SRC_W * scale);
  const H = Math.round(SRC_H * scale);

  const work = document.createElement("canvas");
  work.width = W; work.height = H;
  const ctx = work.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(imgElement, 0, 0, W, H);

  // 1. Segmentation
  const segResult = segmenter.segment(work);
  const catMask = segResult.categoryMask;
  const maskData = catMask.getAsUint8Array();
  const hairMask = new Uint8Array(W * H);
  const faceMask = new Uint8Array(W * H);
  for (let i = 0; i < maskData.length; i++) {
    const label = maskData[i];
    if (label === HAIR_LABEL) hairMask[i] = 1;
    if (label === FACE_SKIN_LABEL || label === BODY_SKIN_LABEL) faceMask[i] = 1;
  }
  catMask.close();

  // 2. (Optional) face-landmark assist — only used to slightly dilate the SEGMENTER's
  //    face-skin mask, never as a hard rectangle. The segmenter already gives us
  //    pixel-accurate hair vs face-skin labels; we trust those.
  let faceBox = null;
  try {
    const faceResult = faceLandmarker.detect(work);
    if (faceResult.faceLandmarks && faceResult.faceLandmarks.length > 0) {
      const lm = faceResult.faceLandmarks[0];
      let minX = W, minY = H, maxX = 0, maxY = 0;
      for (const p of lm) {
        const x = p.x * W, y = p.y * H;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
      faceBox = { x: Math.floor(minX), y: Math.floor(minY), w: Math.ceil(maxX - minX), h: Math.ceil(maxY - minY) };
    }
  } catch (e) {
    // Face detection failed — segmenter labels are still authoritative.
  }

  // 3. Clean the hair mask edges. We do a 2-pixel erosion against the face/body-skin
  //    mask to kill the soft halo where MediaPipe sometimes labels skin as hair.
  //    Particularly important for red-haired subjects (red sits close to skin in the
  //    model's feature space) and tight selfies where hair drapes over the face.
  const cleanedHair = new Uint8Array(W * H);
  // Pass 1: standard 4-neighbour erosion plus reject any hair pixel adjacent to face-skin.
  for (let py = 2; py < H - 2; py++) {
    for (let px = 2; px < W - 2; px++) {
      const idx = py * W + px;
      if (hairMask[idx] !== 1) continue;
      // 4-neighbour hair check.
      if (!(hairMask[idx - 1] && hairMask[idx + 1] && hairMask[idx - W] && hairMask[idx + W])) continue;
      // Reject if ANY pixel within 2px is labelled face/body-skin (not just immediate neighbour).
      let nearSkin = false;
      for (let dy = -2; dy <= 2 && !nearSkin; dy++) {
        for (let dx = -2; dx <= 2 && !nearSkin; dx++) {
          if (faceMask[(py + dy) * W + (px + dx)]) nearSkin = true;
        }
      }
      if (nearSkin) continue;
      cleanedHair[idx] = 1;
    }
  }

  return { hairMask: cleanedHair, faceMask, width: W, height: H, faceBox };
}

// Crop a tight rectangle around the hair pixels — used to send a cropped patch to Gemini.
// Returns a base64 JPEG string.
export function cropHairPatchBase64(imgElement, segResult, quality = 0.75) {
  const { hairMask, width: W, height: H } = segResult;
  let minX = W, minY = H, maxX = 0, maxY = 0;
  for (let py = 0; py < H; py++) {
    for (let px = 0; px < W; px++) {
      if (hairMask[py * W + px]) {
        if (px < minX) minX = px;
        if (py < minY) minY = py;
        if (px > maxX) maxX = px;
        if (py > maxY) maxY = py;
      }
    }
  }
  if (maxX <= minX || maxY <= minY) return null;

  // Map back to source image coordinates (segResult is downscaled to ≤512).
  const SRC_W = imgElement.naturalWidth;
  const SRC_H = imgElement.naturalHeight;
  const sx = (minX / W) * SRC_W;
  const sy = (minY / H) * SRC_H;
  const sw = ((maxX - minX) / W) * SRC_W;
  const sh = ((maxY - minY) / H) * SRC_H;

  // Output dimensions — capped so request stays small.
  const outMax = 640;
  const outScale = Math.min(1, outMax / Math.max(sw, sh));
  const outW = Math.round(sw * outScale);
  const outH = Math.round(sh * outScale);
  const out = document.createElement("canvas");
  out.width = outW; out.height = outH;
  const octx = out.getContext("2d");
  octx.drawImage(imgElement, sx, sy, sw, sh, 0, 0, outW, outH);
  return out.toDataURL("image/jpeg", quality);
}
